import { create } from 'zustand';

import { Nullable } from 'src/types/nullable.type';
import { Document } from 'src/common/interfaces/document.interface';
import { DocumentBlock, DocumentBlockType } from 'src/common/interfaces/document_block.interface';

interface StateVars {
  document: Nullable<Document>;
  initialBlockTypes: any;
}

interface InitialBlockType {
  type: DocumentBlockType;
  attrs: any;
  initialized: boolean;
}

const initialState: StateVars = {
  document: null,
  initialBlockTypes: {},
};

interface StateFns {
  setDocument: (document: Nullable<Document>) => void;
  setDocumentBlocks: (blocks: DocumentBlock[]) => void;
  addDocumentBlock: (block: DocumentBlock, insertIndex: Nullable<number>) => void;
  deleteDocumentBlock: (blockId: number) => void;
  updateDocumentBlock: (blockId: number, documentBlock: DocumentBlock) => void;
  addInitialBlockTypeForBlock: (blockId: number, blockType: DocumentBlockType, attrs: any) => void;
  shouldInitializeBlock: (blockId: number) => boolean;
  getInitialBlockType: (blockId: number) => InitialBlockType;
  markBlockAsInitialized: (blockId: number) => void;
  resetDocumentEditor: () => void;
}

type DocumentEditorState = StateVars & StateFns;

export const useDocumentEditorContext = create<DocumentEditorState>((set: any, get: any) => ({
  ...initialState,

  setDocument: (document: Nullable<Document>) => {
    set((state: any) => ({
      document,
    }));
  },

  setDocumentBlocks: (blocks: any) => {
    set((state: any) => ({
      document: {
        ...state.document,
        blocks,
      },
    }));
  },

  addDocumentBlock: (block: DocumentBlock, insertIndex: Nullable<number>) => {
    set((state: any) => ({
      document: {
        ...state.document,
        blocks:
          insertIndex !== null
            ? [...state.document.blocks.slice(0, insertIndex), block, ...state.document.blocks.slice(insertIndex)]
            : [...state.document.blocks, block],
      },
    }));
  },

  deleteDocumentBlock: (blockId: number) => {
    set((state: any) => ({
      document: {
        ...state.document,
        blocks: state.document.blocks.filter((block: DocumentBlock) => block.id !== blockId),
      },
    }));
  },

  updateDocumentBlock: (blockId: number, documentBlock: DocumentBlock) => {
    set((state: any) => ({
      document: {
        ...state.document,
        blocks: state.document.blocks.map((block: DocumentBlock) => {
          if (block.id === blockId) {
            return documentBlock;
          }
          return block;
        }),
      },
    }));
  },

  addInitialBlockTypeForBlock: (blockId: number, blockType: DocumentBlockType, attrs = {}) => {
    set((state: any) => ({
      initialBlockTypes: {
        ...state.initialBlockTypes,
        [blockId]: {
          type: blockType,
          initialized: false,
          attrs: attrs,
        },
      },
    }));
  },

  shouldInitializeBlock: (blockId: number) => {
    const initialBlockType = get().initialBlockTypes[blockId];
    return initialBlockType && !initialBlockType.initialized;
  },

  getInitialBlockType: (blockId: number) => {
    const initialBlockType = get().initialBlockTypes[blockId];
    return initialBlockType ? initialBlockType : null;
  },

  markBlockAsInitialized: (blockId: number) => {
    set((state: any) => ({
      initialBlockTypes: {
        ...state.initialBlockTypes,
        [blockId]: {
          ...state.initialBlockTypes[blockId],
          initialized: true,
        },
      },
    }));
  },

  resetDocumentEditor: () => {
    set((state: any) => ({
      ...initialState,
    }));
  },
}));
